import React from 'react';
import { View, ViewStyle, StyleProp, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask } from 'react-native-masked-text';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../services/translate';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

interface MyCnpjInputProps {
    viewStyle?: StyleProp<ViewStyle>;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    disabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
}

export default function MyCnpjInput({ viewStyle, required, form, fieldName, customValidations, pattern, labelStyle, disabled }: MyCnpjInputProps) {
    const { styles, theme } = useStyles(stylesheet);

    function validateCNPJ(cnpj: string) {
        if (!cnpj) {
            return false;
        }

        const cleanCnpj = cnpj.replace(/\D/g, '');

        if (cleanCnpj.length !== 14 || !!cleanCnpj.match(/(\d)\1{13}/)) {
            return false;
        }

        const size = cleanCnpj.length - 2;
        const numbers = cleanCnpj.substring(0, size);
        const digits = cleanCnpj.substring(size);

        const calc = (n: string) => {
            let sum = 0;
            let pos = n.length - 7;
            for (let i = n.length; i >= 1; i--) {
                sum += parseInt(n.charAt(n.length - i)) * pos--;
                if (pos < 2) {
                    pos = 9;
                }
            }
            const result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            return result;
        };

        const digit1 = calc(numbers);
        if (digit1 !== parseInt(digits.charAt(0))) {
            return false;
        }

        const digit2 = calc(numbers + digit1);
        if (digit2 !== parseInt(digits.charAt(1))) {
            return false;
        }

        return true;
    }

    const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

    if (required) {
        rules.required = translate('required_field');
    }

    if (pattern) {
        rules.pattern = pattern;
    }

    rules.validate = {
        validateCnpj: (value) => validateCNPJ(value) || translate('invalid_field')
    };

    if (customValidations) {
        rules.validate = {
            ...rules.validate,
            ...customValidations
        };
    }

    return <Controller
        control={form.control}
        name={fieldName}
        rules={rules}
        render={({ field, fieldState }) => (
            <View style={[{ rowGap: 5 }, viewStyle]}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'flex-start' }}>
                    <MyAppText style={labelStyle ? labelStyle : styles.filterText}>CNPJ</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    onBlur={field.onBlur}
                    value={field.value}
                    includeRawValueInChangeText={true}
                    onChangeText={(maskedText, rawText) => {
                        field.onChange(rawText || '');
                    }}
                    type={'cnpj'}
                    style={[styles.filterInput(field.value == '', fieldState.invalid),
                        disabled && styles.disabled
                    ]}
                    placeholder='00.000.000/0000-00'
                    editable={!disabled}
                />
                {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
            </View>
        )}
    />;
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: {
        flex: 1,
        minWidth: 200,
    },
    filterText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },

    filterInput: (isPlaceholder: boolean, isInvalid: boolean) => ({
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderWidth: 1,
        borderRadius: 4,
        borderColor: isInvalid ? '#FF0000' : theme.colors.field.border,
        height: 40,
        minHeight: 40,
        fontSize: 16,
        color: isPlaceholder ? theme.colors.placeholder : theme.colors.field.text,
        padding: 10
    }),
    error: {
        color: '#FF0000',
        fontSize: 10
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
}));
